/**
 * La carte « Interventions » de la fiche d'une machine : ses derniers bons, et le geste « Nouveau bon ».
 *
 * Elle ne montre que ce qui s'y trouve (numéro, nature, date, état) : le détail se lit dans l'écran des bons, où
 * un clic mène. Une machine archivée n'est plus affichée : le serveur refuse d'y créer un bon.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type BonResume } from '../api/client';
import { useDroitsBons } from '../auth/useDroits';
import { LIBELLE_ETAT, classeEtat, jourFr } from '../domaine/interventions';

const NOMBRE_AFFICHE = 5;

export function InterventionsMachine({ assetId }: { assetId: string }) {
  const droits = useDroitsBons();
  const [bons, setBons] = useState<readonly BonResume[] | null>(null);
  const [total, setTotal] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!droits.peutLireBons) return;
    let annule = false;
    setBons(null);
    api
      .bonsDeLaMachine(assetId, NOMBRE_AFFICHE)
      .then((r) => {
        if (annule) return;
        setBons(r.items);
        setTotal(r.total);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Les bons de cette machine n’ont pas pu être lus.');
      });
    return () => {
      annule = true;
    };
  }, [assetId, droits.peutLireBons]);

  if (!droits.peutLireBons) return null;

  return (
    <div className="carte">
      <h2>Interventions</h2>
      <div className="carte-corps">
        {erreur && <div className="message erreur">{erreur}</div>}
        {bons === null && !erreur && <p className="aide">Chargement…</p>}
        {bons !== null && bons.length === 0 && <p className="aide">Aucun bon d’intervention sur cette machine.</p>}
        {bons !== null && bons.length > 0 && (
          <ul className="bons-machine">
            {bons.map((b) => (
              <li key={b.id}>
                <Link to={`/interventions?bon=${b.id}`}>{b.number}</Link>
                <span className="aide">
                  {' '}
                  {b.nature.label} · {jourFr(b.performedOn)}
                </span>{' '}
                <span className={`etat ${classeEtat(b.status)}`}>{LIBELLE_ETAT[b.status]}</span>
                {b.asset && b.asset.id !== assetId && (
                  <span className="aide">
                    {' '}
                    — depuis la fiche fusionnée <Link to={`/machines/${b.asset.id}`}>{b.asset.label ?? 'en double'}</Link>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="saisie-ligne">
          {total > NOMBRE_AFFICHE && (
            <Link className="bouton-lien secondaire-lien" to={`/interventions?machine=${assetId}`}>
              Tous les bons ({total})
            </Link>
          )}
          {droits.peutSaisirBons && (
            <Link className="bouton-lien" to={`/interventions?nouveau=${assetId}`}>
              Nouveau bon
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
